import { useEffect, useState } from "react";
import toast from "../../utils/toast.js";
import { addRoute, updateRoute } from "../../services/carsService.js";

const CITIES = ["Lucknow", "Kanpur", "Delhi", "Agra", "Jaipur", "Varanasi", "Prayagraj", "Noida"];

const DEFAULTS = {
  routeFrom: "",
  routeTo: "",
  departures: [],
  specialTrips: [],
  rateFront: "449",
  rateMiddle: "349",
  rateRear: "",
  rateFull: "1899",
  allowPerSeat: true,
  allowFullCab: true,
  autoConfirm: true,
};

// Adds a new route to an already-published car, or edits one specific
// existing route — used from the "My Cars" card, separate from the
// onboarding wizard (which only ever handles a car's first route).
export default function AddRouteModal({ isOpen, car, route, onClose, onSaved }) {
  const isEditing = !!route;
  const isMuv = car?.carType === "7-seater";

  const [routeFrom, setRouteFrom] = useState(DEFAULTS.routeFrom);
  const [routeTo, setRouteTo] = useState(DEFAULTS.routeTo);
  const [departures, setDepartures] = useState(DEFAULTS.departures);
  const [newDepartureTime, setNewDepartureTime] = useState("");
  const [specialTrips, setSpecialTrips] = useState(DEFAULTS.specialTrips);
  const [newTripDate, setNewTripDate] = useState("");
  const [newTripTime, setNewTripTime] = useState("");
  const [rateFront, setRateFront] = useState(DEFAULTS.rateFront);
  const [rateMiddle, setRateMiddle] = useState(DEFAULTS.rateMiddle);
  const [rateRear, setRateRear] = useState(DEFAULTS.rateRear);
  const [rateFull, setRateFull] = useState(DEFAULTS.rateFull);
  const [allowPerSeat, setAllowPerSeat] = useState(DEFAULTS.allowPerSeat);
  const [allowFullCab, setAllowFullCab] = useState(DEFAULTS.allowFullCab);
  const [autoConfirm, setAutoConfirm] = useState(DEFAULTS.autoConfirm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (!isOpen) return;
    setNewDepartureTime("");
    setNewTripDate("");
    setNewTripTime("");
    setError("");

    if (route) {
      setRouteFrom(route.fromCity || "");
      setRouteTo(route.toCity || "");
      setDepartures(Array.isArray(route.dailyDepartures) ? route.dailyDepartures : []);
      setSpecialTrips(Array.isArray(route.specialTrips) ? route.specialTrips : []);
      setRateFront(String(route.rateCard?.front ?? "449"));
      setRateMiddle(String(route.rateCard?.middle ?? "349"));
      setRateRear(route.rateCard?.rear ? String(route.rateCard.rear) : "");
      setRateFull(String(route.rateCard?.fullCab ?? "1899"));
      setAllowPerSeat(route.allowPerSeatSharing ?? true);
      setAllowFullCab(route.allowFullCabBooking ?? true);
      setAutoConfirm(route.autoConfirmBookings ?? true);
    } else {
      setRouteFrom(DEFAULTS.routeFrom);
      setRouteTo(DEFAULTS.routeTo);
      setDepartures(DEFAULTS.departures);
      setSpecialTrips(DEFAULTS.specialTrips);
      setRateFront(DEFAULTS.rateFront);
      setRateMiddle(DEFAULTS.rateMiddle);
      setRateRear(DEFAULTS.rateRear);
      setRateFull(DEFAULTS.rateFull);
      setAllowPerSeat(DEFAULTS.allowPerSeat);
      setAllowFullCab(DEFAULTS.allowFullCab);
      setAutoConfirm(DEFAULTS.autoConfirm);
    }
  }, [isOpen, route]);

  if (!isOpen) return null;

  function addDeparture() {
    if (!newDepartureTime || departures.includes(newDepartureTime)) return;
    setDepartures((prev) => [...prev, newDepartureTime].sort());
    setNewDepartureTime("");
  }

  function removeDeparture(time) {
    setDepartures((prev) => prev.filter((t) => t !== time));
  }

  function addSpecialTrip() {
    if (!newTripDate || !newTripTime) return;
    const exists = specialTrips.some((t) => t.date === newTripDate && t.time === newTripTime);
    if (exists) return;
    setSpecialTrips((prev) =>
      [...prev, { date: newTripDate, time: newTripTime }].sort((a, b) =>
        a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date),
      ),
    );
    setNewTripDate("");
    setNewTripTime("");
  }

  function removeSpecialTrip(date, time) {
    setSpecialTrips((prev) => prev.filter((t) => !(t.date === date && t.time === time)));
  }

  function formatTripPillLabel(date, time) {
    const d = new Date(`${date}T00:00:00`);
    const dateLabel = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    return `${dateLabel}, ${time}`;
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!routeFrom || !routeTo || routeFrom === routeTo) {
      setError("Please select two different cities for the route.");
      return;
    }
    if (!departures.length && !specialTrips.length) {
      setError("Please add at least one daily departure or a one-time special trip.");
      return;
    }
    if (!rateFront || !rateMiddle || (isMuv && !rateRear) || !rateFull) {
      setError("Please fill in every seat-tier price.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const payload = {
        fromCity: routeFrom,
        toCity: routeTo,
        dailyDepartures: departures,
        specialTrips,
        rateCard: {
          front: Number(rateFront),
          middle: Number(rateMiddle),
          rear: isMuv ? Number(rateRear) : 0,
          fullCab: Number(rateFull),
        },
        allowPerSeatSharing: allowPerSeat,
        allowFullCabBooking: allowFullCab,
        autoConfirmBookings: autoConfirm,
      };

      const res = isEditing
        ? await updateRoute(route._id, payload)
        : await addRoute(car._id, payload);
      if (!res.success) {
        toast.error(res.message);
        return;
      }
      toast.success(isEditing ? "Route updated." : "Route added.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't save the route.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="wizard-overlay open">
      <div className="wizard-modal">
        <button type="button" className="wizard-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <h2>{isEditing ? "Edit route" : "Add a new route"}</h2>
        <p className="wizard-sub">
          {car?.carModel} · {car?.registrationNo} —{" "}
          {isEditing ? "editing this route" : "adding an additional route to this car"}.
        </p>

        <form onSubmit={handleSave}>
          <div className="field-row-2">
            <div className="field">
              <label htmlFor="routeFromModal">Route from</label>
              <select
                id="routeFromModal"
                value={routeFrom}
                onChange={(e) => setRouteFrom(e.target.value)}
              >
                <option value="">Select city</option>
                {CITIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="routeToModal">Route to</label>
              <select
                id="routeToModal"
                value={routeTo}
                onChange={(e) => setRouteTo(e.target.value)}
              >
                <option value="">Select city</option>
                {CITIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label htmlFor="newDepartureTimeModal">Daily departures</label>
            <div className="field-row-2">
              <input
                type="time"
                id="newDepartureTimeModal"
                value={newDepartureTime}
                onChange={(e) => setNewDepartureTime(e.target.value)}
              />
              <button type="button" className="btn btn-outline" onClick={addDeparture}>
                + Add
              </button>
            </div>
            <div className="time-pills">
              {departures.map((time) => (
                <button
                  type="button"
                  key={time}
                  className="time-pill selected"
                  onClick={() => removeDeparture(time)}
                  title="Tap to remove"
                >
                  {time} ×
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label htmlFor="newTripDateModal">Special one-time trips</label>
            <div className="field-row-2">
              <input
                type="date"
                id="newTripDateModal"
                min={today}
                value={newTripDate}
                onChange={(e) => setNewTripDate(e.target.value)}
              />
              <input
                type="time"
                id="newTripTimeModal"
                value={newTripTime}
                onChange={(e) => setNewTripTime(e.target.value)}
              />
            </div>
            <button type="button" className="btn btn-outline" onClick={addSpecialTrip}>
              + Add trip
            </button>
            <div className="time-pills">
              {specialTrips.map((trip) => (
                <button
                  type="button"
                  key={`${trip.date}-${trip.time}`}
                  className="time-pill selected"
                  onClick={() => removeSpecialTrip(trip.date, trip.time)}
                  title="Tap to remove"
                >
                  {formatTripPillLabel(trip.date, trip.time)} ×
                </button>
              ))}
              {!specialTrips.length && <span className="badge-warn">No special trips added</span>}
            </div>
          </div>

          <h3 className="rate-card-heading">Per-seat rate card (₹ / seat)</h3>
          <div className={`rate-row ${!allowPerSeat ? "disabled" : ""}`}>
            <div>
              <strong>Front (co-driver)</strong>
              <span>Best view · 1 seat</span>
            </div>
            <div className="rate-input">
              <span>₹</span>
              <input type="number" value={rateFront} onChange={(e) => setRateFront(e.target.value)} />
            </div>
          </div>
          <div className={`rate-row ${!allowPerSeat ? "disabled" : ""}`}>
            <div>
              <strong>Middle row</strong>
              <span>Window & middle seats</span>
            </div>
            <div className="rate-input">
              <span>₹</span>
              <input
                type="number"
                value={rateMiddle}
                onChange={(e) => setRateMiddle(e.target.value)}
              />
            </div>
          </div>
          {isMuv && (
            <div className={`rate-row ${!allowPerSeat ? "disabled" : ""}`}>
              <div>
                <strong>Rear row</strong>
                <span>Bench seating</span>
              </div>
              <div className="rate-input">
                <span>₹</span>
                <input
                  type="number"
                  value={rateRear}
                  onChange={(e) => setRateRear(e.target.value)}
                />
              </div>
            </div>
          )}
          <div className={`rate-row ${!allowFullCab ? "disabled" : ""}`}>
            <div>
              <strong>Full cab (exclusive)</strong>
              <span>Entire car, door-to-door</span>
            </div>
            <div className="rate-input">
              <span>₹</span>
              <input type="number" value={rateFull} onChange={(e) => setRateFull(e.target.value)} />
            </div>
          </div>

          <div className="toggle-row">
            <strong>Allow per-seat sharing</strong>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={allowPerSeat}
                onChange={(e) => setAllowPerSeat(e.target.checked)}
              />
              <span className="toggle-switch-track" />
            </label>
          </div>
          <div className="toggle-row">
            <strong>Allow full cab booking</strong>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={allowFullCab}
                onChange={(e) => setAllowFullCab(e.target.checked)}
              />
              <span className="toggle-switch-track" />
            </label>
          </div>
          <div className="toggle-row">
            <strong>Auto-confirm bookings</strong>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={autoConfirm}
                onChange={(e) => setAutoConfirm(e.target.checked)}
              />
              <span className="toggle-switch-track" />
            </label>
          </div>

          <p className="field-error">{error}</p>

          <div className="wizard-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : isEditing ? "Save route" : "Add route"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
